import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Globe, Loader2 } from 'lucide-react';
import { useTranslation, mapPublishError, buildStoreUrl } from '@dukanti/shared';
import type { PublishErrorKey } from '@dukanti/shared';
import { usePublishStore } from '@/hooks/use-publish-store';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PublishStoreDialogProps {
  /** Store to publish. Server re-checks `is_store_admin(storeId)`
   *  inside the RPC — the client-supplied id is never trusted. */
  readonly storeId: string;
  /** Slug used to build the public URL preview (`buildStoreUrl(slug)`). */
  readonly storeSlug: string;
  /** Controlled open state. Parent (`dashboard-route`) owns this
   *  because the dialog is driven from the `PublishStoreCard`'s CTA. */
  readonly open: boolean;
  /** Fires on open/close transitions from the AlertDialog primitive. */
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for publishing a store (AC3).
 *
 * Pattern mirrors `delete-product-dialog.tsx` verbatim:
 *   - Controlled `AlertDialog` (NOT `Dialog`) so it blocks keyboard
 *     focus until the merchant explicitly cancels or confirms.
 *   - `errorKey` state holds the last failed mapping so the inline
 *     banner and the toast stay consistent with what the user saw.
 *   - Pending-state label swap: "Confirm publish" → "Publishing…" with
 *     a spinner; the button is `aria-busy` and `disabled` during the
 *     mutation so users can't double-fire it.
 *   - On success: sonner `toast.success('…toasts.published')`, close
 *     the dialog, clear the errorKey.
 *   - On failure: sonner `toast.error(mapped)`, keep the dialog open,
 *     render the inline banner, let the user retry.
 *
 * Seven error branches are rendered (one per `PublishErrorKey`), each
 * with a unique `data-testid`:
 *   - `noStockedProducts`    → banner + translated "Add a product" link
 *     CTA routing to `/products/new`. This is the F-004 inventory gate.
 *   - `missingContactMethod` → banner + translated "Go to store
 *     settings" link CTA routing to `/dashboard/settings?fix=contact`.
 *     F-007's publish gate (SQLSTATE P0003). The `?fix=contact` query
 *     param triggers the settings route's deep-link banner and
 *     auto-focuses the WhatsApp input.
 *   - `missingLogo` → banner + translated "Upload logo" link CTA
 *     routing to `/dashboard/settings`.
 *   - `permissionDenied`  → banner only. No recovery CTA: the merchant
 *     is trying to publish a store they don't administer, and the
 *     fix is outside the dialog (re-auth, switch store, contact us).
 *   - `storeNotFound`     → banner only. The Rev 2 M2 fix: a
 *     soft-deleted store raised `P0002`. The copy reads "Refresh the
 *     page" — we deliberately do NOT render a refresh button so the
 *     merchant sees the real state (stale session) and can decide.
 *   - `networkFailed`     → banner only. The user retries by clicking
 *     Confirm again.
 *   - `unknown`           → banner with generic translated copy.
 *
 * URL preview: rendered as an LTR pill inside the description block so
 * the `http://` prefix and slashes don't get flipped by the Arabic
 * parent direction (same pitfall as `PublishedStoreCard`).
 *
 * Test-ids (F-004 design §5.5):
 *   - `publish-store-dialog`                           — root dialog
 *   - `publish-store-dialog-confirm`                   — Confirm button
 *   - `publish-store-dialog-cancel`                    — Cancel button
 *   - `publish-store-dialog-error`                     — any error banner
 *   - `publish-store-dialog-error-<key>`               — per-branch banner
 *   - `publish-store-dialog-add-product-link`          — noStockedProducts CTA
 *   - `public-url-preview`                             — LTR URL pill
 *   - `store-status-loading`                           — present only
 *     while the mutation is pending (W2 — the parent card's assertion
 *     that this testid never appears on the idle dashboard relies on
 *     it being scoped inside the dialog, not the card).
 */
export function PublishStoreDialog({
  storeId,
  storeSlug,
  open,
  onOpenChange,
}: PublishStoreDialogProps) {
  const { t, dir } = useTranslation();
  const publishMutation = usePublishStore();
  const [errorKey, setErrorKey] = useState<PublishErrorKey | null>(null);

  const isPending = publishMutation.isPending;
  const publicUrl = buildStoreUrl(storeSlug);

  // Reset the banner whenever the dialog is closed so the next open
  // starts clean. We don't clear on the *same* open cycle because the
  // inline banner needs to persist between the failure and the next
  // retry click (that's the whole point of keeping the dialog open
  // after an error).
  useEffect(() => {
    if (!open) {
      setErrorKey(null);
    }
  }, [open]);

  function handleOpenChange(next: boolean): void {
    // Block closing while pending — the RPC round-trip is short but
    // non-zero, and letting the user dismiss mid-flight would leave
    // them staring at a dashboard that might flip any moment without
    // explanation.
    if (isPending) return;
    if (!next) setErrorKey(null);
    onOpenChange(next);
  }

  function handleConfirm(): void {
    if (isPending) return;
    setErrorKey(null);
    publishMutation.mutate(
      { storeId },
      {
        onSuccess: () => {
          toast.success(t('admin.publishStore.toasts.published'));
          onOpenChange(false);
        },
        onError: (err) => {
          const mapped = mapPublishError(err);
          setErrorKey(mapped);
          toast.error(t(mapped));
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent dir={dir} data-testid="publish-store-dialog" className="gap-0 p-8">
        {/* Icon wrap — Globe in a circular Zaatar wash, echoing the
            Stitch design's warm iconography. */}
        <div
          aria-hidden="true"
          className="bg-zaatar-wash text-zaatar-deep mx-auto mb-4 flex size-14 items-center justify-center rounded-full"
        >
          <Globe className="size-7" strokeWidth={1.75} />
        </div>

        <AlertDialogHeader className="mb-3">
          <AlertDialogTitle className="text-center">
            {t('admin.publishStore.modal.title')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogDescription className="text-sand-600 mb-4 text-center text-sm leading-relaxed">
          {t('admin.publishStore.modal.description')}
        </AlertDialogDescription>

        {/* URL preview — LTR pill so `http://` renders left-to-right
            even inside an RTL dialog. */}
        <div
          data-testid="public-url-preview"
          dir="ltr"
          className="border-sand-200 bg-sand-100 text-zaatar-deep mb-6 truncate rounded-lg border px-4 py-3 text-center font-mono text-sm font-medium"
        >
          {publicUrl}
        </div>

        {/* Inline error banner — rendered once per failed mutation. */}
        {errorKey ? (
          <div
            role="alert"
            data-testid="publish-store-dialog-error"
            data-error-key={errorKey}
            className={cn(
              'border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card mb-6',
              'flex items-start gap-2 border p-3 text-start text-sm',
            )}
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <div className="flex-1">
              {/* Per-branch testid so each mapping can be asserted
                  independently. We split `admin.publishStore.errors.<key>`
                  from the enum-friendly suffix. */}
              {errorKey === 'admin.publishStore.errors.noStockedProducts' ? (
                <>
                  <p data-testid="publish-store-dialog-error-noStockedProducts">
                    {t('admin.publishStore.errors.noStockedProducts')}
                  </p>
                  <Link
                    to="/products/new"
                    data-testid="publish-store-dialog-add-product-link"
                    className="text-zaatar hover:text-zaatar-deep mt-2 inline-block text-start font-medium underline"
                  >
                    {t('admin.publishStore.errors.noStockedProductsCta')}
                  </Link>
                </>
              ) : errorKey === 'admin.publishStore.errors.missingContactMethod' ? (
                <>
                  <p data-testid="publish-store-dialog-error-missingContactMethod">
                    {t('admin.publishStore.errors.missingContactMethod')}
                  </p>
                  <Link
                    to="/dashboard/settings?fix=contact"
                    data-testid="publish-store-dialog-contact-settings-link"
                    className="text-zaatar hover:text-zaatar-deep mt-2 inline-block text-start font-medium underline"
                  >
                    {t('admin.publishStore.errors.missingContactMethodCta')}
                  </Link>
                </>
              ) : errorKey === 'admin.publishStore.errors.missingLogo' ? (
                <>
                  <p data-testid="publish-store-dialog-error-missingLogo">
                    {t('admin.publishStore.errors.missingLogo')}
                  </p>
                  <Link
                    to="/dashboard/settings"
                    data-testid="publish-store-dialog-logo-settings-link"
                    className="text-zaatar hover:text-zaatar-deep mt-2 inline-block text-start font-medium underline"
                  >
                    {t('admin.publishStore.errors.missingLogoCta')}
                  </Link>
                </>
              ) : errorKey === 'admin.publishStore.errors.subscriptionRequired' ? (
                <p data-testid="publish-store-dialog-error-subscriptionRequired">
                  {t('admin.publishStore.errors.subscriptionRequired')}
                </p>
              ) : errorKey === 'admin.publishStore.errors.activeProductLimitReached' ? (
                <p data-testid="publish-store-dialog-error-activeProductLimitReached">
                  {t('admin.publishStore.errors.activeProductLimitReached')}
                </p>
              ) : errorKey === 'common.errors.permissionDenied' ? (
                <p data-testid="publish-store-dialog-error-permissionDenied">
                  {t('common.errors.permissionDenied')}
                </p>
              ) : errorKey === 'common.errors.storeNotFound' ? (
                <p data-testid="publish-store-dialog-error-storeNotFound">
                  {t('common.errors.storeNotFound')}
                </p>
              ) : errorKey === 'common.errors.networkFailed' ? (
                <p data-testid="publish-store-dialog-error-networkFailed">
                  {t('common.errors.networkFailed')}
                </p>
              ) : (
                <p data-testid="publish-store-dialog-error-unknown">{t('common.errors.unknown')}</p>
              )}
            </div>
          </div>
        ) : null}

        <AlertDialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
            data-testid="publish-store-dialog-cancel"
            className="text-sand-600 hover:bg-sand-100 hover:text-sand-700"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={isPending}
            onClick={handleConfirm}
            data-testid="publish-store-dialog-confirm"
            aria-busy={isPending || undefined}
          >
            {isPending ? (
              <>
                {/* Loading sentinel — only present during the mutation.
                    W2: the parent card asserts this testid NEVER appears
                    on the idle dashboard. The assertion holds whether or
                    not the dialog is open because the element is scoped
                    to the pending state, not to open state. */}
                <span data-testid="store-status-loading" className="sr-only">
                  {t('admin.publishStore.ctaLabelSubmitting')}
                </span>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                <span>{t('admin.publishStore.ctaLabelSubmitting')}</span>
              </>
            ) : (
              <>
                <Globe aria-hidden="true" className="size-4" strokeWidth={1.75} />
                <span>{t('admin.publishStore.modal.confirm')}</span>
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
